import { useState } from 'react';
import { doc, serverTimestamp, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import {
  addIncident,
  checkForCategorySuggestion,
} from '../../../services/incidentService';
import { saveBehaviorIncident } from '../../../services/behaviorIncidentService';
import { buildFallbackContextSnapshot } from './context/incidentContextBuilder';
import { buildBehaviorIncidentPayload } from './buildBehaviorIncidentPayload';

const getLocalDayKey = (date) => {
  const target = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  const year = target.getFullYear();
  const month = String(target.getMonth() + 1).padStart(2, '0');
  const day = String(target.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildGenericIncidentData = ({
  incidentTime,
  incidentTypeOverride,
  customCategoryType,
  isCustomCategory,
  incidentName,
  severity,
  notes,
  selectedTriggers,
  triggerLabels,
  user,
}) => {
  const incidentTimestamp = new Date(incidentTime);
  const triggerTextSummary = triggerLabels.join(', ');

  return {
    type: isCustomCategory ? customCategoryType : (incidentTypeOverride || 'other'),
    customIncidentName: isCustomCategory ? '' : incidentName.trim(),
    severity,
    remedy: triggerTextSummary || 'General observation and monitoring',
    triggerSummary: triggerTextSummary,
    suspectedTriggers: selectedTriggers,
    triggerNotes: notes.trim(),
    description: incidentName.trim(),
    incidentDateTime: incidentTimestamp.toISOString(),
    incidentDayKey: getLocalDayKey(incidentTimestamp),
    entryDateLabel: incidentTimestamp.toDateString(),
    authorId: user?.uid,
    authorName: user?.displayName || user?.email?.split('@')[0] || 'User',
    authorEmail: user?.email,
  };
};

export const useIncidentCaptureActions = ({
  childId,
  childName,
  onSaved,
  onCategoryCreated,
  isCustomCategory,
  customCategoryType,
  incidentTypeOverride,
  entryId,
  initialData,
  isEditMode,
  incidentForm,
  incidentTime,
  incidentName,
  severity,
  notes,
  selectedTriggers,
  contextItems,
  patternInsight,
  user,
  triggerGroups,
  quickLogConfig = null,
}) => {
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categorySuggestion, setCategorySuggestion] = useState(null);

  const handleSave = () => {
    incidentForm.submitForm(
      async () => {
        const triggerLabels = selectedTriggers
          .map((triggerKey) => triggerGroups.find((group) => group.key === triggerKey)?.label)
          .filter(Boolean);

        const isBehaviorFlow = incidentTypeOverride === 'behavioral' || incidentTypeOverride === 'behavior';

        if (isBehaviorFlow) {
          const contextSnapshot = buildFallbackContextSnapshot(contextItems, patternInsight);
          const {
            incidentTimestamp,
            incidentData,
            commonBehaviorDoc,
          } = buildBehaviorIncidentPayload({
            childId,
            incidentTime,
            incidentTypeOverride,
            customCategoryType,
            isCustomCategory,
            incidentName,
            severity,
            notes,
            selectedTriggers,
            triggerLabels,
            user,
            contextSnapshot,
            quickLogConfig,
          });

          if (isEditMode && (entryId || initialData?.id)) {
            const targetId = entryId || initialData?.id;
            await updateDoc(doc(db, 'dailyLogs', targetId), {
              ...commonBehaviorDoc,
              updatedAt: serverTimestamp(),
              notes: deleteField(),
            });

            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
                detail: {
                  childId,
                  entryId: targetId,
                  timestamp: incidentTimestamp,
                  collection: 'dailyLogs',
                },
              }));
            }

            onSaved();
            return;
          }

          const savedEntry = await saveBehaviorIncident({
            childId,
            incidentData,
            userId: user?.uid,
            fallbackContextSnapshot: contextSnapshot,
          });

          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('captureez:timeline-entry-created', {
              detail: {
                ...savedEntry.entry,
                timestamp: incidentTimestamp,
                childId,
              },
            }));

            window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
              detail: {
                childId,
                entryId: savedEntry.id,
                timestamp: incidentTimestamp,
                collection: 'dailyLogs',
              },
            }));
          }

          onSaved();
          return;
        }

        const incidentData = buildGenericIncidentData({
          incidentTime,
          incidentTypeOverride,
          customCategoryType,
          isCustomCategory,
          incidentName,
          severity,
          notes,
          selectedTriggers,
          triggerLabels,
          user,
        });

        await addIncident(childId, incidentData, true, childName || 'child');

        if (!isCustomCategory && !incidentTypeOverride) {
          const suggestionCheck = await checkForCategorySuggestion(childId, incidentName.trim());

          if (suggestionCheck.shouldSuggest) {
            setCategorySuggestion(suggestionCheck.suggestion);
            setShowCategoryModal(true);
            return;
          }
        }

        onSaved();
      },
      { incidentName, isCustomCategory }
    );
  };

  const handleCategoryCreated = () => {
    setShowCategoryModal(false);
    setCategorySuggestion(null);

    if (onCategoryCreated) {
      onCategoryCreated();
    }

    onSaved();
  };

  const handleCategoryModalClose = () => {
    setShowCategoryModal(false);
    setCategorySuggestion(null);
    onSaved();
  };

  return {
    showCategoryModal,
    categorySuggestion,
    handleSave,
    handleCategoryCreated,
    handleCategoryModalClose,
  };
};
